"use client";

import { useEffect, useRef, useState } from 'react';
import { auth } from '../config/firebaseConfig';
import useUserStore from '@/store/userStore';
import useCartStore from '@/store/cartStore';
import authService from '@/services/authService';

// Переносим переменную внутрь компонента и используем useState
const AuthInitializer = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  
  const {
    subscribeAuthChanges,
    resetLoadingState,
    loading,
    isLoggedIn,
    user,
    fetchUserProfile,
    logout
  } = useUserStore();

  const {
    initAuthListener,
    fetchCartContents
  } = useCartStore();

  const isMounted = useRef(false);
  const logoutAttemptRef = useRef(false);

  // Проверяем URL на наличие параметра logout
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const url = new URL(window.location.href);
    const isLogout = url.searchParams.get('logout') === 'true';
    
    if (isLogout && !logoutAttemptRef.current) {
      logoutAttemptRef.current = true;
      
      // Очищаем параметр из URL
      url.searchParams.delete('logout');
      window.history.replaceState({}, '', url.toString());
      
      // Очищаем куки через document.cookie
      const cookiesToClear = ['jwt_access_token', 'jwt_refresh_token', 'auth_token', 'firebase_token'];
      cookiesToClear.forEach(name => {
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
      });
      
      // При наличии URL-параметра logout=true выполняем полную очистку
      authService.clearTokens();
    }
  }, []);

  // Инициализация JWT токенов и настройка проверки срока действия
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Используем sessionStorage для отслеживания статуса авторизации в текущей сессии
        const sessionAuthStatus = sessionStorage.getItem('auth_initialized');
        
        if (auth.currentUser) {
          // Если пользователь авторизован в Firebase, обмениваем токен на JWT
          await authService.exchangeFirebaseToken();
          if (!sessionAuthStatus) {
            sessionStorage.setItem('auth_initialized', 'true');
          }
        } else {
          // Проверяем, есть ли у нас сохраненные JWT токены
          const accessToken = authService.getAccessToken();
          const refreshToken = authService.getRefreshToken();
          
          if (accessToken || refreshToken) {
            // Проверяем валидность существующих JWT токенов
            const token = await authService.getValidToken();
            if (token) {
              // Если токен валидный, получаем данные пользователя
              const authStatus = await authService.checkAuthStatus();
              if (authStatus.authenticated && authStatus.user) {
                useUserStore.getState().setUserManually(authStatus.user);
                if (!sessionAuthStatus) {
                  sessionStorage.setItem('auth_initialized', 'true');
                }
              }
            }
          }
        }
        
        // Настройка периодической проверки JWT токена (каждые 5 минут)
        const checkTokenInterval = setInterval(async () => {
          if (auth.currentUser || authService.getAccessToken()) {
            await authService.getValidToken();
          }
        }, 5 * 60 * 1000);
        
        return () => clearInterval(checkTokenInterval);
      } catch (error) {
        console.error('❌ AuthInitializer: Error initializing JWT tokens:', error);
      }
    };

    initAuth();
    
    // Обновление JWT токена при возвращении на вкладку
    const visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        if (auth.currentUser || authService.getAccessToken()) {
          authService.getValidToken().then(token => {
            if (token) {
              // Если токен есть, но пользователь не залогинен - запрашиваем статус
              if (!useUserStore.getState().isLoggedIn) {
                authService.checkAuthStatus().then(status => {
                  if (status.authenticated && status.user) {
                    useUserStore.getState().setUserManually(status.user);
                  }
                });
              }
            }
          });
        }
      }
    };

    document.addEventListener('visibilitychange', visibilityHandler);

    return () => {
      document.removeEventListener('visibilitychange', visibilityHandler);
    };
  }, []);

  // Инициализация аутентификации и корзины
  useEffect(() => {
    isMounted.current = true;

    // Инициализируем аутентификацию всегда при монтировании компонента 
    // с проверкой на уже выполненную инициализацию
    try {
      // Если инициализация уже была выполнена
      if (isInitialized) {
        // Запрашиваем профиль, если необходимо
        if (isLoggedIn && user && !user.photoURL && fetchUserProfile) {
          fetchUserProfile();
        }
        return () => {};
      }
      
      // Устанавливаем флаг инициализации
      setIsInitialized(true);

      // Подписка на изменение состояния авторизации
      let unsubscribe = () => {};
      if (subscribeAuthChanges) {
        unsubscribe = subscribeAuthChanges();
      }

      // Инициализация слушателя корзины
      if (initAuthListener) {
        initAuthListener();
      }

      // Первоначальная загрузка содержимого корзины
      let cartFetchTimeout = setTimeout(() => {
        if (isMounted.current && fetchCartContents) {
          fetchCartContents(true).catch(err => console.error('❌ AuthInitializer: Error in initial cart fetch:', err));
        }
      }, 1000);

      // Таймаут безопасности для сброса состояния загрузки
      const safetyTimeout = setTimeout(() => {
        if (loading && resetLoadingState) {
          resetLoadingState();
        }

        // Сброс флагов операций корзины
        useCartStore.setState({
          fetchInProgress: false,
          addInProgress: false,
          removeInProgress: false,
          clearInProgress: false
        });
      }, 10000);

      // Проверка авторизации при каждой загрузке страницы
      const checkAuthTimeout = setTimeout(async () => {
        const token = await authService.getValidToken();
        if (token && !isLoggedIn) {
          try {
            const authStatus = await authService.checkAuthStatus();
            if (authStatus.authenticated && authStatus.user) {
              useUserStore.getState().setUserManually(authStatus.user);
            }
          } catch (error) {
            console.error('Error checking auth on page load:', error);
          }
        }
      }, 500);

      return () => {
        isMounted.current = false;
        clearTimeout(cartFetchTimeout);
        clearTimeout(safetyTimeout);
        clearTimeout(checkAuthTimeout);

        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      };
    } catch (error) {
      console.error('❌ AuthInitializer: Error in initialization:', error);
      setIsInitialized(false);
      return () => {};
    }
  }, [
    fetchCartContents,
    fetchUserProfile,
    initAuthListener,
    isLoggedIn,
    loading,
    resetLoadingState,
    subscribeAuthChanges,
    user,
    isInitialized
  ]);

  return null;
};

export default AuthInitializer;